import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getCustomers, getSites, getHeatPumps } from "../lib/sync";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Plus,
  MapPin,
  Mail,
  Phone,
  User,
  Thermometer,
  ChevronRight,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import MapLink from "../components/MapLink";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [sites, setSites] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [siteOpen, setSiteOpen] = useState(false);
  const [pumpOpen, setPumpOpen] = useState(false);
  const [siteForm, setSiteForm] = useState({});
  const [pumpForm, setPumpForm] = useState({});

  const load = useCallback(async () => {
    const custs = await getCustomers().catch(() => []);
    setCustomer(custs.find((c) => c.id === id) || null);
    setSites(await getSites(id).catch(() => []));
    setPumps(await getHeatPumps({ customer_id: id }).catch(() => []));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const saveSite = async () => {
    if (!siteForm.name) return toast.error("Site name required");
    try {
      await api.post("/sites", { ...siteForm, customer_id: id });
      toast.success("Site added");
      setSiteOpen(false);
      setSiteForm({});
      load();
    } catch (e) {
      toast.error("Could not add site");
    }
  };

  const savePump = async () => {
    if (!pumpForm.site_id) return toast.error("Choose a site");
    try {
      await api.post("/heatpumps", { ...pumpForm, customer_id: id });
      toast.success("Heat pump added");
      setPumpOpen(false);
      setPumpForm({});
      load();
    } catch (e) {
      toast.error("Could not add heat pump");
    }
  };

  if (!customer)
    return <p className="py-10 text-center text-slate-400">Loading…</p>;

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/customers")} className="flex items-center gap-1 text-sm font-semibold text-slate-500" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Customers
      </button>

      <Card className="p-5">
        <h1 className="font-heading text-xl font-extrabold text-slate-900">{customer.name}</h1>
        <div className="mt-3 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
          {customer.contact_person && <Info Icon={User} text={customer.contact_person} />}
          {customer.phone && <Info Icon={Phone} text={customer.phone} />}
          {customer.email && <Info Icon={Mail} text={customer.email} />}
          {customer.billing_address && (
            <MapLink address={customer.billing_address} testid="map-customer" className="text-sm" />
          )}
        </div>
        {customer.notes && (
          <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-500">{customer.notes}</p>
        )}
      </Card>

      {/* sites */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-slate-900">Sites</h2>
          <Button size="sm" variant="outline" onClick={() => setSiteOpen(true)} data-testid="add-site-btn">
            <Plus className="mr-1 h-4 w-4" /> Site
          </Button>
        </div>
        <div className="space-y-2">
          {sites.map((s) => (
            <Card key={s.id} className="p-4" data-testid={`site-${s.id}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-900">{s.name}</span>
              </div>
              {s.address ? (
                <div className="ml-0 mt-1">
                  <MapLink address={s.address} testid={`map-site-${s.id}`} className="text-xs" />
                </div>
              ) : null}
            </Card>
          ))}
          {sites.length === 0 && <p className="text-sm text-slate-400">No sites yet.</p>}
        </div>
      </section>

      {/* heat pumps */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-slate-900">Heat pumps</h2>
          <Button size="sm" variant="outline" onClick={() => setPumpOpen(true)} data-testid="add-pump-btn">
            <Plus className="mr-1 h-4 w-4" /> Heat pump
          </Button>
        </div>
        <div className="space-y-2">
          {pumps.map((p) => (
            <Card
              key={p.id}
              onClick={() => navigate(`/heatpump/${p.id}`)}
              className="flex cursor-pointer items-center justify-between p-4 hover:border-primary/40"
              data-testid={`pump-${p.id}`}
            >
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Thermometer className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-sm font-bold text-slate-900">
                    {p.manufacturer} {p.model}
                  </div>
                  <div className="text-xs text-slate-500">S/N {p.serial_number}</div>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-300" />
            </Card>
          ))}
          {pumps.length === 0 && <p className="text-sm text-slate-400">No heat pumps yet.</p>}
        </div>
      </section>

      <Button
        className="h-12 w-full"
        onClick={() => navigate(`/report/new?customer=${id}`)}
        data-testid="new-report-for-customer"
      >
        <FileText className="mr-2 h-4 w-4" /> New service report for this customer
      </Button>

      {/* add site dialog */}
      <Dialog open={siteOpen} onOpenChange={setSiteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New site</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Site name *" value={siteForm.name} onChange={(v) => setSiteForm({ ...siteForm, name: v })} testid="site-name" />
            <Field label="Address" value={siteForm.address} onChange={(v) => setSiteForm({ ...siteForm, address: v })} testid="site-address" />
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={siteForm.notes || ""} onChange={(e) => setSiteForm({ ...siteForm, notes: e.target.value })} data-testid="site-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button className="h-11 w-full" onClick={saveSite} data-testid="save-site-btn">Save site</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* add pump dialog */}
      <Dialog open={pumpOpen} onOpenChange={setPumpOpen}>
        <DialogContent className="max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>New heat pump</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Site *</Label>
              <select
                value={pumpForm.site_id || ""}
                onChange={(e) => setPumpForm({ ...pumpForm, site_id: e.target.value })}
                data-testid="pump-site"
                className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm"
              >
                <option value="">Select site…</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            {[
              ["manufacturer", "Manufacturer"],
              ["model", "Model"],
              ["serial_number", "Serial number"],
              ["installation_date", "Installation date"],
              ["commissioning_date", "Commissioning date"],
              ["warranty_details", "Warranty details"],
              ["indoor_unit", "Indoor unit details"],
              ["outdoor_unit", "Outdoor unit details"],
              ["refrigerant_type", "Refrigerant type"],
            ].map(([k, label]) => (
              <Field
                key={k}
                label={label}
                type={k.includes("date") ? "date" : "text"}
                value={pumpForm[k]}
                onChange={(v) => setPumpForm({ ...pumpForm, [k]: v })}
                testid={`pump-${k}`}
              />
            ))}
          </div>
          <DialogFooter>
            <Button className="h-11 w-full" onClick={savePump} data-testid="save-pump-btn">Save heat pump</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ Icon, text }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-slate-400" />
      <span>{text}</span>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-11" />
    </div>
  );
}
